import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { sample } from "lodash-es";
import {
  clearProfileSession,
  setProfileSession,
  setProfileSwitching,
} from "@onmo/graphql";
import {
  InventoryAvatarData,
  ProfileTokenType,
  UpdateProfileActionInput,
} from "@onmo/graphql/graphql";
import { ProfileStore } from "../../hooks/useProfiles";
import { useCurrentProfile } from "../../hooks/useProfileSession";
import {
  useApplyAvatar,
  useFetchFreeAvatars,
} from "../../hooks/useSelectAvatarQuery";
import { isGamepadMode } from "../../hooks/useCrossInput";
import { useGamepadContext } from "../../contexts/GamepadProvider";
import { PageRoutes } from "../../constants/routes";
import {
  nextFrame,
  refreshDataForNewProfile,
} from "../../models/profileSwitch";
import {
  isValidProfileName,
  Profile,
  ProfileDraft,
} from "../../models/profile";
import { ProfileFormField } from "../../components/common/ProfileFormPopup";
import {
  getProfileErrorCode,
  getProfileErrorKey,
  isPinProofRejected,
  OTP_RESEND_SECONDS,
  ProfileErrorCode,
} from "../../models/profileErrors";

export type PinScope = "profile" | "account";

export type PinReason =
  | "enterProfile"
  | "editProfile"
  | "addProfile"
  | "removeProfile"
  | "manageAccountLocks"
  | "disableKidMode"
  | "saveProfile";

export type AccountPinPurpose =
  | { action: "create"; reason: PinReason }
  | { action: "reset"; reason: PinReason; from: PinScope }
  | { action: "update" };

export type FlowDialog =
  | { type: "none" }
  | { type: "profileForm"; focusField?: ProfileFormField }
  | { type: "chooseAvatar" }
  | {
      type: "enterPin";
      scope: PinScope;
      reason: PinReason;
      isResettingPin?: boolean;
    }
  | { type: "createProfilePin"; resetFor?: PinReason }
  | { type: "accountPinIntro"; purpose: AccountPinPurpose }
  | { type: "verificationCode"; purpose: AccountPinPurpose }
  | { type: "setAccountPin"; purpose: AccountPinPurpose }
  | { type: "accountLocks"; focusUpdateButton?: boolean }
  | { type: "removeProfile" };

// A short-lived token proving a PIN was entered; isMain when it was the Account PIN
type PinProof = { token: string; isMain: boolean };

const SWITCH_MIN_MS = 500;
const SWITCH_MAX_MS = 8000;

const wait = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const withTimeout = (promise: Promise<void>, ms: number) =>
  Promise.race([promise, wait(ms)]);

const EMPTY_DRAFT: ProfileDraft = { name: "", isKid: false, hasLock: false };

export const useProfileFlow = (store: ProfileStore) => {
  const client = useApolloClient();
  const { setActiveElement, setActiveDefaultElement } = useGamepadContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [dialog, setDialog] = useState<FlowDialog>({ type: "none" });
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [draft, setDraft] = useState<ProfileDraft>(EMPTY_DRAFT);
  const [isNameTouched, setIsNameTouched] = useState(false);
  const [pinProof, setPinProof] = useState<PinProof | null>(null);
  // Avatars go to the profile the token belongs to, so the edited profile
  // needs its own token; an Account PIN proof belongs to the main profile
  const [avatarToken, setAvatarToken] = useState<string | null>(null);
  const [accountLocks, setAccountLocks] = useState({
    profileLock: false,
    creationLock: false,
  });
  const [code, setCode] = useState("");
  const [errorKey, setErrorKey] = useState("");
  const [nameErrorKey, setNameErrorKey] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [isOtpLockedOut, setIsOtpLockedOut] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const { freeAvatars } = useFetchFreeAvatars();
  const applyAvatar = useApplyAvatar();
  const { currentProfile } = useCurrentProfile();

  const show = (next: FlowDialog) => {
    setDialog(next);
    setCode("");
    setErrorKey("");
    setNameErrorKey("");
  };

  const close = () => show({ type: "none" });

  const showForm = (focusField?: ProfileFormField) =>
    show({ type: "profileForm", focusField });

  useEffect(() => {
    if (selectedProfile || draft.inventoryId || !freeAvatars.length) return;
    const avatar = sample(freeAvatars);
    if (!avatar) return;
    setDraft((current) => ({
      ...current,
      inventoryId: avatar.id,
      avatar: (avatar.data as InventoryAvatarData)?.url,
    }));
  }, [selectedProfile, draft.inventoryId, freeAvatars]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  const startSession = (profile: Profile | null) => {
    setSelectedProfile(profile);
    setDraft(
      profile
        ? {
            name: profile.name,
            isKid: profile.isKid,
            hasLock: profile.hasLock,
            avatar: profile.avatar,
          }
        : EMPTY_DRAFT,
    );
    setIsNameTouched(false);
    setPinProof(null);
    setAvatarToken(null);
  };

  // The page swaps its data in place, so the old focus may be gone
  const refocusAfterSwitch = (isFromPicker: boolean) => {
    if (!isGamepadMode()) return;
    const switcher = document.querySelector<HTMLElement>(
      '[data-gamepad="menu-profile-switcher"]',
    );
    if (switcher && !isFromPicker) setActiveElement(switcher);
    else setActiveDefaultElement();
  };

  // Identity changes here: the page stays on screen while its data is
  // refetched for the new profile and swapped in. The destination page
  // mounts first so its queries are among the refetched.
  const leavePicker = async () => {
    close();
    setProfileSwitching(true);
    const isFromPicker = location.pathname === PageRoutes.WhoIsPlaying;
    if (isFromPicker) {
      navigate(location.state?.from ?? PageRoutes.Home, {
        replace: true,
        flushSync: true,
      });
    }
    try {
      await nextFrame();
      await Promise.all([
        withTimeout(refreshDataForNewProfile(client), SWITCH_MAX_MS),
        wait(SWITCH_MIN_MS),
      ]);
    } finally {
      setProfileSwitching(false);
      requestAnimationFrame(() => refocusAfterSwitch(isFromPicker));
    }
  };

  const enterProfile = (token: string) => {
    setProfileSession(token);
    leavePicker();
  };

  // A profile without its own PIN can only be opened with the Account PIN
  const pinScopeFor = (profile: Profile): PinScope =>
    profile.hasPin ? "profile" : "account";

  const requireAccountPin = (reason: PinReason) => {
    if (pinProof?.isMain) return continueAfterPin(reason);
    if (store.hasAccountPin) {
      return show({ type: "enterPin", scope: "account", reason });
    }
    show({ type: "accountPinIntro", purpose: { action: "create", reason } });
  };

  const continueAfterPin = (reason: PinReason, proof = pinProof) => {
    switch (reason) {
      case "enterProfile":
        return;
      case "saveProfile":
        return saveProfile(proof);
      case "editProfile":
      case "addProfile":
        return showForm();
      case "removeProfile":
        return show({ type: "removeProfile" });
      case "disableKidMode":
        setDraft((current) => ({ ...current, isKid: false }));
        return showForm();
      case "manageAccountLocks":
        setAccountLocks({
          profileLock: draft.hasLock,
          creationLock: store.isCreationLocked,
        });
        return show({ type: "accountLocks" });
    }
  };

  const goBackFrom = (reason: PinReason) => {
    switch (reason) {
      case "enterProfile":
      case "editProfile":
      case "addProfile":
        return close();
      case "manageAccountLocks":
        return showForm("profileLock");
      case "removeProfile":
        return showForm("remove");
      case "saveProfile":
      case "disableKidMode":
        return showForm();
    }
  };

  // Who's playing grid

  const selectProfile = async (profile: Profile) => {
    if (isBusy) return;
    setSelectedProfile(profile);
    if (profile.hasLock) {
      return show({
        type: "enterPin",
        scope: pinScopeFor(profile),
        reason: "enterProfile",
      });
    }
    setIsBusy(true);
    try {
      const token = await store.generateToken({ id: profile.id });
      enterProfile(token);
    } catch (error) {
      if (getProfileErrorCode(error) === ProfileErrorCode.MissingPin) {
        show({
          type: "enterPin",
          scope: pinScopeFor(profile),
          reason: "enterProfile",
        });
      } else {
        failWith(error);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const editProfile = (profile: Profile) => {
    startSession(profile);
    if (profile.hasLock) {
      return show({
        type: "enterPin",
        scope: pinScopeFor(profile),
        reason: "editProfile",
      });
    }
    showForm();
  };

  const addProfile = () => {
    startSession(null);
    if (!store.hasAccountPin) {
      return show({
        type: "accountPinIntro",
        purpose: { action: "create", reason: "addProfile" },
      });
    }
    showForm();
  };

  // Profile form: Save asks for a PIN only when the opening PIN is not enough

  const changeName = (name: string) => {
    setIsNameTouched(true);
    setNameErrorKey("");
    setDraft((current) => ({ ...current, name }));
  };

  const toggleKidMode = (isKid: boolean) => {
    if (!isKid && selectedProfile?.isKid) {
      return requireAccountPin("disableKidMode");
    }
    setDraft((current) => ({ ...current, isKid }));
  };

  const toggleProfileLock = (hasLock: boolean) => {
    if (hasLock && !selectedProfile?.hasLock) {
      return show({ type: "createProfilePin" });
    }
    setDraft((current) => ({ ...current, hasLock, lockPin: undefined }));
  };

  const manageProfileLock = () => requireAccountPin("manageAccountLocks");

  const askToRemoveProfile = () => requireAccountPin("removeProfile");

  const openAvatar = () => show({ type: "chooseAvatar" });

  const closeAvatar = () => showForm("avatar");

  const selectAvatar = (inventoryId: string, url: string) => {
    setDraft((current) => ({ ...current, inventoryId, avatar: url }));
    showForm("avatar");
  };

  const buildUpdateActions = (profile: Profile, name: string) => {
    const actions: UpdateProfileActionInput[] = [];
    if (name !== profile.name) {
      actions.push({ editDetails: { username: name } });
    }
    if (profile.isKid && !draft.isKid) {
      actions.push({ disableKidMode: true });
    }
    if (draft.hasLock !== profile.hasLock) {
      actions.push(
        draft.hasLock
          ? { lock: { newLockPin: draft.lockPin } }
          : { unlock: true },
      );
    }
    return actions;
  };

  // Turning kid mode or the lock off needs the Account PIN; other sensitive
  // changes also accept the profile's own PIN
  const hasEnoughProof = (
    profile: Profile,
    actions: UpdateProfileActionInput[],
    proof: PinProof | null,
  ) => {
    const needsAccountPin = actions.some((action) => action.disableKidMode);
    if (needsAccountPin) return !!proof?.isMain;
    const needsAnyPin = actions.some(
      (action) => action.unlock || (action.editDetails && profile.hasLock),
    );
    return !needsAnyPin || !!proof;
  };

  const saveAvatar = async (profile: Profile) => {
    const avatar = freeAvatars.find((item) => item.id === draft.inventoryId);
    if (!avatar || draft.avatar === profile.avatar) return;
    const token =
      profile.id === currentProfile?.id
        ? undefined
        : avatarToken ?? (await store.generateToken({ id: profile.id }));
    await applyAvatar(avatar, profile.id, token);
  };

  // createProfile returns no id, so the new profile is found by its unique name
  const createProfile = async (name: string) => {
    await store.createProfile({ username: name, isKidProfile: draft.isKid });
    const { data } = await store.refetch();
    const avatar = freeAvatars.find((item) => item.id === draft.inventoryId);
    const profile = data.profiles.find((item) => item.username === name);
    if (!avatar || !profile) return;
    try {
      const token = await store.generateToken({ id: profile.id });
      await applyAvatar(avatar, profile.id, token);
    } catch (error) {
      console.error(error);
    }
  };

  const askPinToSave = () =>
    show({ type: "enterPin", scope: "account", reason: "saveProfile" });

  const saveProfile = async (proof: PinProof | null) => {
    if (isBusy) return;
    const name = draft.name.trim();
    const actions = selectedProfile
      ? buildUpdateActions(selectedProfile, name)
      : [];
    if (selectedProfile && !hasEnoughProof(selectedProfile, actions, proof)) {
      return askPinToSave();
    }
    setIsBusy(true);
    try {
      if (selectedProfile) {
        await saveAvatar(selectedProfile);
        for (const action of actions) {
          await store.updateProfile(selectedProfile.id, action, proof?.token);
        }
      } else {
        await createProfile(name);
      }
      close();
    } catch (error) {
      if (isPinProofRejected(error)) {
        setPinProof(null);
        setAvatarToken(null);
        return askPinToSave();
      }
      showForm();
      setNameErrorKey(getProfileErrorKey(error));
      await store.refetch().catch(() => undefined);
    } finally {
      setIsBusy(false);
    }
  };

  const submitForm = () => saveProfile(pinProof);

  // PIN and verification code steps

  const changeCode = (value: string) => {
    setCode(value);
    setErrorKey("");
  };

  const failWith = (error: unknown) => {
    const code = getProfileErrorCode(error);
    if (code === ProfileErrorCode.OtpLockdown) setIsOtpLockedOut(true);
    if (code === ProfileErrorCode.OtpCooldown) setResendIn(OTP_RESEND_SECONDS);
    setErrorKey(getProfileErrorKey(error));
  };

  const proveAccountPin = async (accountPin: string): Promise<PinProof> => {
    const token = await store.generateToken({
      id: store.mainProfileId ?? "",
      accountPin,
      type: ProfileTokenType.PinVerification,
    });
    if (selectedProfile?.isOwner) {
      setAvatarToken(token);
    } else if (selectedProfile?.hasLock) {
      setAvatarToken(
        await store.generateToken({
          id: selectedProfile.id,
          accountPin,
          type: ProfileTokenType.PinVerification,
        }),
      );
    }
    return { token, isMain: true };
  };

  const proveProfilePin = async (
    profile: Profile,
    pin: string,
  ): Promise<PinProof> => {
    const token = await store.generateToken({
      id: profile.id,
      pin,
      type: ProfileTokenType.PinVerification,
    });
    setAvatarToken(token);
    return { token, isMain: profile.isOwner };
  };

  const submitPin = async (
    scope: PinScope,
    reason: PinReason,
    isResettingPin = false,
  ) => {
    if (!selectedProfile || isBusy) return;
    setIsBusy(true);
    try {
      if (isResettingPin) {
        setPinProof(await proveAccountPin(code));
        return show({ type: "createProfilePin", resetFor: reason });
      }
      if (reason === "enterProfile") {
        const token = await store.generateToken(
          scope === "profile"
            ? { id: selectedProfile.id, pin: code }
            : { id: selectedProfile.id, accountPin: code },
        );
        return enterProfile(token);
      }
      const proof =
        scope === "account"
          ? await proveAccountPin(code)
          : await proveProfilePin(selectedProfile, code);
      setPinProof(proof);
      await continueAfterPin(reason, proof);
    } catch (error) {
      failWith(error);
    } finally {
      setIsBusy(false);
    }
  };

  const canSwitchPinScope = (reason: PinReason) =>
    store.hasAccountPin &&
    !!selectedProfile?.hasPin &&
    (reason === "enterProfile" || reason === "editProfile");

  const switchPinScope = (scope: PinScope, reason: PinReason) =>
    show({
      type: "enterPin",
      scope: scope === "profile" ? "account" : "profile",
      reason,
    });

  const createProfilePin = () => {
    setDraft((current) => ({ ...current, hasLock: true, lockPin: code }));
    showForm("profileLock");
  };

  const cancelProfilePin = () => showForm("profileLock");

  const sendVerificationCode = async (purpose: AccountPinPurpose) => {
    if (isBusy) return;
    setIsOtpLockedOut(false);
    setIsBusy(true);
    try {
      const { requestId } = await store.requestOtp();
      setRequestId(requestId);
      setResendIn(OTP_RESEND_SECONDS);
      show({ type: "verificationCode", purpose });
    } catch (error) {
      failWith(error);
    } finally {
      setIsBusy(false);
    }
  };

  const resendVerificationCode = async () => {
    if (isBusy || resendIn > 0 || isOtpLockedOut) return;
    setCode("");
    setErrorKey("");
    setIsBusy(true);
    try {
      const { requestId } = await store.requestOtp();
      setRequestId(requestId);
      setResendIn(OTP_RESEND_SECONDS);
    } catch (error) {
      failWith(error);
    } finally {
      setIsBusy(false);
    }
  };

  const submitVerificationCode = async (purpose: AccountPinPurpose) => {
    if (isBusy || isOtpLockedOut) return;
    setIsBusy(true);
    try {
      await store.verifyOtp(requestId, code);
      show({ type: "setAccountPin", purpose });
    } catch (error) {
      failWith(error);
    } finally {
      setIsBusy(false);
    }
  };

  const saveAccountPin = async (purpose: AccountPinPurpose) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await store.saveAccountPin(code, requestId);
      await store.refetch();
      if (purpose.action === "update") {
        return show({ type: "accountLocks", focusUpdateButton: true });
      }
      if (purpose.reason === "addProfile") return showForm();
      // Forgot PIN while entering a profile: the new PIN opens it straight away
      if (purpose.reason === "enterProfile" && selectedProfile) {
        const token = await store.generateToken({
          id: selectedProfile.id,
          accountPin: code,
        });
        return enterProfile(token);
      }
      const proof = await proveAccountPin(code);
      setPinProof(proof);
      await continueAfterPin(purpose.reason, proof);
    } catch (error) {
      failWith(error);
    } finally {
      setIsBusy(false);
    }
  };

  const cancelAccountPin = (purpose: AccountPinPurpose) => {
    if (purpose.action === "update") {
      return show({ type: "accountLocks", focusUpdateButton: true });
    }
    if (purpose.action === "reset") {
      return show({
        type: "enterPin",
        scope: purpose.from,
        reason: purpose.reason,
      });
    }
    goBackFrom(purpose.reason);
  };

  // Forgot, reset and create all set the Account PIN through the same OTP flow
  const forgotAccountPin = (reason: PinReason, from: PinScope) =>
    show({
      type: "accountPinIntro",
      purpose: { action: "reset", reason, from },
    });

  const forgotProfilePin = (reason: PinReason) =>
    show({ type: "enterPin", scope: "account", reason, isResettingPin: true });

  const saveProfilePin = async (reason: PinReason) => {
    if (!selectedProfile || isBusy) return;
    setIsBusy(true);
    try {
      await store.updateProfile(
        selectedProfile.id,
        { unlock: true },
        pinProof?.token,
      );
      await store.updateProfile(selectedProfile.id, {
        lock: { newLockPin: code },
      });
      if (reason !== "enterProfile") return showForm();
      const token = await store.generateToken({
        id: selectedProfile.id,
        pin: code,
      });
      enterProfile(token);
    } catch (error) {
      failWith(error);
    } finally {
      setIsBusy(false);
    }
  };

  // Account PIN and Locks

  const updateAccountPin = () =>
    show({ type: "accountPinIntro", purpose: { action: "update" } });

  const toggleAccountProfileLock = (profileLock: boolean) =>
    setAccountLocks((current) => ({ ...current, profileLock }));

  const toggleCreationLock = (creationLock: boolean) =>
    setAccountLocks((current) => ({ ...current, creationLock }));

  const saveAccountLocks = () => {
    store.saveCreationLock(accountLocks.creationLock);
    setDraft((current) => ({ ...current, hasLock: accountLocks.profileLock }));
    showForm("profileLock");
  };

  const cancelAccountLocks = () => showForm("profileLock");

  // Remove profile

  const keepProfile = () => showForm("remove");

  const confirmRemoveProfile = async () => {
    if (!selectedProfile || isBusy) return;
    setIsBusy(true);
    try {
      await store.updateProfile(
        selectedProfile.id,
        { disable: true },
        pinProof?.token,
      );
    } catch (error) {
      if (isPinProofRejected(error)) {
        setPinProof(null);
        return show({
          type: "enterPin",
          scope: "account",
          reason: "removeProfile",
        });
      }
      showForm("remove");
      return setNameErrorKey(getProfileErrorKey(error));
    } finally {
      setIsBusy(false);
    }

    close();
    if (selectedProfile.id === currentProfile?.id) {
      clearProfileSession();
      client.resetStore().catch(() => undefined);
    } else {
      store.refetch().catch(() => undefined);
    }
  };

  const isEditing = !!selectedProfile;
  const isOwner = !!selectedProfile?.isOwner;
  const isNameValid = isValidProfileName(draft.name);

  return {
    dialog,
    draft,
    code,
    errorKey,
    nameErrorKey,
    resendIn,
    isOtpLockedOut,
    isBusy,
    accountLocks,
    phone: store.phone,
    form: {
      mode: isEditing ? "edit" : "new",
      canSubmit: isNameValid && !isBusy,
      isNameInvalid: isNameTouched && !isNameValid,
      showKidMode: !isEditing || !!selectedProfile?.isKid,
      profileLockControl: !isEditing
        ? undefined
        : isOwner
        ? "manage"
        : "toggle",
      showRemove: isEditing && !isOwner,
    } as const,
    close,
    selectProfile,
    editProfile,
    addProfile,
    changeName,
    toggleKidMode,
    toggleProfileLock,
    manageProfileLock,
    askToRemoveProfile,
    openAvatar,
    closeAvatar,
    selectAvatar,
    submitForm,
    changeCode,
    submitPin,
    goBackFrom,
    canSwitchPinScope,
    switchPinScope,
    createProfilePin,
    cancelProfilePin,
    sendVerificationCode,
    resendVerificationCode,
    submitVerificationCode,
    saveAccountPin,
    cancelAccountPin,
    forgotAccountPin,
    forgotProfilePin,
    saveProfilePin,
    updateAccountPin,
    toggleAccountProfileLock,
    toggleCreationLock,
    saveAccountLocks,
    cancelAccountLocks,
    keepProfile,
    confirmRemoveProfile,
  };
};

export type ProfileFlow = ReturnType<typeof useProfileFlow>;
